import type * as Immutable from 'immutable';
import { createMigration } from './utils';

/**
 * Renames the per-object stagger from a scene-duration-relative `percentageOffset` to a fixed-ms
 * `staggerDelay` (and `mirrorPercentageOffset` → `mirrorStaggerDelay`). For every existing
 * animation, we compute `staggerDelay = percentageOffset × parentContainerDurationMs`, which
 * freezes today's behavior on each container at its current duration.
 *
 * Parent container = closest enclosing duration source for the animation:
 *   - subscene-level children → that subscene's `duration`
 *   - scene-level children and scene `stickers` → sum of the scene's subscene durations
 *   - scene `textPositionPresets` → these are position variants of real scene assets; correlate
 *     by `name` to the actual asset and use its container (scene duration if it's a scene-level
 *     child, else the first subscene's duration)
 *   - sceneGroup `captions.asset` (and `decorations`) → sum of all slides' durations whose
 *     `sceneGroupId` matches the sceneGroup's id
 *
 * `migrateScene` is self-contained — a scene knows its own subscene durations, so external
 * callers using `Migrator.updateSceneData` / `updateSceneDesignData` get correct results.
 * SceneGroup caption migration lives in a private `migrateSceneGroupCaptions` helper rather
 * than on the migration object: it needs a duration that can only be derived from the slides
 * collection, which only `migrateVideo` has in scope.
 */

const STYLE_FIELDS = ['normalStyle', 'highlightStyle', 'spokenWordStyle'] as const;

const LEGACY_GRAPHIC_FIELDS = ['colorGraphics', 'alphaGraphics'] as const;

function migrateAnimationList(
    map: Immutable.Map<string, unknown>,
    path: Array<string | number>,
    parentDurationMs: number,
): void {
    const list = map.getIn(path) as Immutable.List<Immutable.Map<string, unknown>> | undefined;
    if (!list) return;
    list.forEach((_anim, i) => {
        const animPath = [...path, i];
        const anim = map.getIn(animPath) as Immutable.Map<string, unknown>;
        const hasPercentageOffset = anim.has('percentageOffset');
        const hasMirror = anim.has('mirrorPercentageOffset');
        if (!hasPercentageOffset && !hasMirror) return;

        let updated = anim;
        if (hasPercentageOffset) {
            const pct = (anim.get('percentageOffset') as number | undefined) ?? 0;
            updated = updated
                .set('staggerDelay', pct * parentDurationMs)
                .delete('percentageOffset');
        }
        if (hasMirror) {
            const mirror = (anim.get('mirrorPercentageOffset') as boolean | undefined) ?? false;
            updated = updated.set('mirrorStaggerDelay', mirror).delete('mirrorPercentageOffset');
        }
        map.setIn(animPath, updated);
    });
}

function migrateAsset(
    map: Immutable.Map<string, unknown>,
    assetPath: Array<string | number>,
    parentDurationMs: number,
): void {
    const asset = map.getIn(assetPath) as Immutable.Map<string, unknown> | undefined;
    if (!asset) return;

    const objectAnimation = asset.get('objectAnimation') as Immutable.List<unknown> | undefined;
    if (objectAnimation) {
        migrateAnimationList(map, [...assetPath, 'objectAnimation'], parentDurationMs);
    }

    STYLE_FIELDS.forEach((styleField) => {
        const style = asset.get(styleField) as Immutable.Map<string, unknown> | undefined;
        if (!style) return;

        if (style.get('animationConfigurations')) {
            migrateAnimationList(
                map,
                [...assetPath, styleField, 'animationConfigurations'],
                parentDurationMs,
            );
        }

        const graphics = style.get('graphics') as Immutable.List<unknown> | undefined;
        if (graphics) {
            graphics.forEach((_graphic, gIndex) => {
                const graphic = map.getIn([...assetPath, styleField, 'graphics', gIndex]) as
                    | Immutable.Map<string, unknown>
                    | undefined;
                if (!graphic) return;
                if (graphic.get('animationConfigurations')) {
                    migrateAnimationList(
                        map,
                        [...assetPath, styleField, 'graphics', gIndex, 'animationConfigurations'],
                        parentDurationMs,
                    );
                }
            });
        }
    });

    LEGACY_GRAPHIC_FIELDS.forEach((graphicField) => {
        const graphics = asset.get(graphicField) as Immutable.List<unknown> | undefined;
        if (!graphics) return;
        graphics.forEach((_graphic, gIndex) => {
            const graphic = map.getIn([...assetPath, graphicField, gIndex]) as
                | Immutable.Map<string, unknown>
                | undefined;
            if (!graphic) return;
            if (graphic.get('animation')) {
                migrateAnimationList(
                    map,
                    [...assetPath, graphicField, gIndex, 'animation'],
                    parentDurationMs,
                );
            }
        });
    });
}

function migrateChildren(
    map: Immutable.Map<string, unknown>,
    childrenPath: Array<string | number>,
    parentDurationMs: number,
): void {
    const children = map.getIn(childrenPath) as Immutable.List<unknown> | undefined;
    if (!children) return;
    children.forEach((_child, i) => {
        migrateAsset(map, [...childrenPath, i], parentDurationMs);
    });
}

function getSceneDuration(scene: Immutable.Map<string, unknown>): number {
    const subslides = scene.get('subslides') as
        | Immutable.List<Immutable.Map<string, unknown>>
        | undefined;
    if (!subslides) return 0;
    return subslides.reduce((sum, s) => sum + ((s.get('duration') as number | undefined) ?? 0), 0);
}

/**
 * Migrates a sceneGroup's caption asset + decorations. `sceneGroupDurationMs` is the sum of
 * durations of all slides in this sceneGroup — it has to be supplied by the caller because
 * sceneGroup data doesn't carry its own duration (it lives on the slides). Called from
 * `migrateVideo`, which computes the value from the slides it iterates.
 */
function migrateSceneGroupCaptions(
    sceneGroup: Immutable.Map<string, unknown>,
    sceneGroupDurationMs: number,
): Immutable.Map<string, unknown> {
    return sceneGroup.withMutations((map) => {
        const captionsAsset = map.getIn(['captions', 'asset']) as
            | Immutable.Map<string, unknown>
            | undefined;
        if (captionsAsset) {
            migrateAsset(map, ['captions', 'asset'], sceneGroupDurationMs);
        }

        const decorations = map.getIn(['captions', 'decorations']) as
            | Immutable.List<unknown>
            | undefined;
        if (decorations) {
            migrateChildren(map, ['captions', 'decorations'], sceneGroupDurationMs);
        }
    });
}

export const migrationTo9_240 = createMigration({
    migrateVideo(video) {
        return video.withMutations((map) => {
            const slides = map.get('slides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;

            const sceneGroupDurations = new Map<string, number>();
            if (slides) {
                slides.forEach((scene) => {
                    const sgId = scene.get('sceneGroupId') as string | undefined;
                    if (!sgId) return;
                    const current = sceneGroupDurations.get(sgId) ?? 0;
                    sceneGroupDurations.set(sgId, current + getSceneDuration(scene));
                });
            }

            const sceneGroups = map.get('sceneGroups') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (sceneGroups) {
                sceneGroups.forEach((sg, sgIdx) => {
                    const sgId = sg.get('id') as string | undefined;
                    const duration = sgId ? (sceneGroupDurations.get(sgId) ?? 0) : 0;
                    map.setIn(['sceneGroups', sgIdx], migrateSceneGroupCaptions(sg, duration));
                });
            }

            if (slides) {
                slides.forEach((scene, sceneIdx) => {
                    map.setIn(['slides', sceneIdx], this.migrateScene(scene));
                });
            }
        });
    },

    migrateScene(scene, _isSceneDesign) {
        return scene.withMutations((map) => {
            const sceneDuration = getSceneDuration(map);

            migrateChildren(map, ['children'], sceneDuration);

            // Stickers are scene-level assets serialized in their own array, so they share
            // the scene duration as their container.
            migrateChildren(map, ['stickers'], sceneDuration);

            const subslides = map.get('subslides') as
                | Immutable.List<Immutable.Map<string, unknown>>
                | undefined;
            if (subslides) {
                subslides.forEach((subslide, subIdx) => {
                    const subsceneDuration = (subslide.get('duration') as number | undefined) ?? 0;
                    migrateChildren(map, ['subslides', subIdx, 'children'], subsceneDuration);
                });
            }

            // textPositionPresets store position variants of real scene assets. The same asset
            // lives either at the scene level (→ scene duration) or in the first subscene
            // (→ that subscene's duration). Correlate by name to pick the right container.
            const presets = map.get('textPositionPresets') as
                | Immutable.Map<string, Immutable.List<Immutable.Map<string, unknown>>>
                | undefined;
            if (presets) {
                const sceneChildNames = childNames(map.get('children'));
                const firstSubsceneDuration =
                    (subslides?.first()?.get('duration') as number | undefined) ?? 0;

                presets.forEach((assets, position) => {
                    assets.forEach((asset, i) => {
                        const name = asset.get('name') as string | undefined;
                        const duration =
                            name && sceneChildNames.has(name)
                                ? sceneDuration
                                : firstSubsceneDuration;
                        migrateAsset(map, ['textPositionPresets', position, i], duration);
                    });
                });
            }
        });
    },
});

function childNames(children: unknown): Set<string> {
    const names = new Set<string>();
    const list = children as Immutable.List<Immutable.Map<string, unknown>> | undefined;
    if (!list) return names;
    list.forEach((child) => {
        const name = child.get('name') as string | undefined;
        if (name) names.add(name);
    });
    return names;
}
